'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, forwardRef } from "react";
import { ClassNameConfigurator, prepareForSlot } from "@mui/base";
import { Button as BaseButton } from "@mui/base/Button";
import { cva } from "class-variance-authority";
import { cn } from "../../common/helpers.js";
import { Icon } from "../Icon/index.js";
const classes = cva([
    'flex',
    'items-center',
    'justify-center',
    'text-center',
    'gap-2',
    'font-sans',
    'font-semibold',
    'relative',
    'box-border',
    'outline-none',
    'before:outline-offset-2',
    'before:outline-2',
    'before:focus-visible:outline',
    'appearance-none',
    'before:transition',
    'before:duration-200',
    'before:absolute',
    'before:inset-0',
    'before:z-[-1]',
    'z-[1]',
    'before:rounded-full',
    'transition-colors',
    'select-none'
], {
    variants: {
        variant: {
            filled: [],
            outlined: [
                'before:border'
            ],
            flat: []
        },
        color: {
            primary: [
                'before:outline-primary'
            ],
            light: [
                'before:outline-white'
            ],
            transparent: [
                'before:bg-transparent',
                'before:outline-primary'
            ]
        },
        size: {
            xs: [
                'h-8',
                'min-w-[2rem]',
                'text-sm',
                'leading-none'
            ],
            sm: [
                'h-10',
                'min-w-[2.5rem]',
                'text-sm',
                'leading-tight'
            ],
            md: [
                'h-[3.75rem]',
                'min-w-[3.75rem]',
                'text-normal',
                'leading-snug'
            ]
        },
        width: {
            auto: [],
            sm: [],
            md: [],
            lg: [],
            full: []
        },
        shape: {
            rounded: [],
            circle: []
        },
        disabled: {
            true: [
                'cursor-not-allowed'
            ],
            false: [
                'cursor-pointer'
            ]
        },
        loading: {
            true: [],
            false: []
        }
    },
    compoundVariants: [
        // Color
        {
            variant: 'filled',
            color: 'primary',
            loading: false,
            className: [
                'before:bg-primary',
                'text-white'
            ]
        },
        {
            variant: 'filled',
            color: 'light',
            loading: false,
            className: [
                'before:bg-white',
                'text-gray-700'
            ]
        },
        {
            variant: 'filled',
            color: 'transparent',
            className: [
                'text-gray-700'
            ]
        },
        {
            variant: 'outlined',
            color: 'primary',
            loading: false,
            className: [
                'before:bg-white',
                'before:border-primary',
                'text-primary'
            ]
        },
        {
            variant: 'outlined',
            color: 'light',
            loading: false,
            className: [
                'before:bg-transparent',
                'before:border-white',
                'text-white'
            ]
        },
        {
            variant: 'outlined',
            color: 'transparent',
            className: [
                'before:border-gray-200',
                'text-gray-700'
            ]
        },
        {
            variant: 'filled',
            color: 'primary',
            loading: true,
            className: [
                'before:bg-gray-700/20',
                'text-gray-700'
            ]
        },
        {
            variant: 'filled',
            color: 'light',
            loading: true,
            className: [
                'before:bg-white',
                'text-gray-700',
                'opacity-60'
            ]
        },
        {
            variant: 'outlined',
            color: 'primary',
            loading: true,
            className: [
                'before:bg-white',
                'before:border-gray-700/20',
                'text-gray-700'
            ]
        },
        {
            variant: 'outlined',
            color: 'light',
            loading: true,
            className: [
                'before:bg-transparent',
                'before:border-white',
                'text-white',
                'opacity-60'
            ]
        },
        // Color Opacity
        {
            disabled: true,
            loading: false,
            className: [
                'opacity-20'
            ]
        },
        {
            color: 'light',
            disabled: true,
            loading: false,
            className: [
                'opacity-60'
            ]
        },
        // Size
        {
            size: 'xs',
            shape: 'rounded',
            className: [
                'px-4'
            ]
        },
        {
            size: 'sm',
            shape: 'rounded',
            className: [
                'px-5'
            ]
        },
        {
            size: 'md',
            shape: 'rounded',
            className: [
                'px-6'
            ]
        },
        {
            size: 'xs',
            shape: 'circle',
            className: [
                'w-8'
            ]
        },
        {
            size: 'sm',
            shape: 'circle',
            className: [
                'w-10'
            ]
        },
        {
            size: 'md',
            shape: 'circle',
            className: [
                'w-[3.75rem]'
            ]
        },
        // Width
        {
            width: 'auto',
            shape: 'rounded',
            className: [
                'w-fit'
            ]
        },
        {
            width: 'sm',
            shape: 'rounded',
            className: [
                'w-full',
                'md:w-[140px]'
            ]
        },
        {
            width: 'md',
            shape: 'rounded',
            className: [
                'w-full',
                'md:w-[200px]'
            ]
        },
        {
            width: 'lg',
            shape: 'rounded',
            className: [
                'w-full',
                'md:w-[310px]'
            ]
        },
        {
            width: 'full',
            shape: 'rounded',
            className: [
                'w-full'
            ]
        },
        // Animation
        {
            variant: [
                'filled',
                'outlined'
            ],
            color: [
                'primary',
                'light'
            ],
            shape: 'rounded',
            disabled: false,
            loading: false,
            className: [
                'before:hover:scale-x-[1.015]',
                'before:hover:scale-y-[1.035]'
            ]
        },
        {
            variant: [
                'filled',
                'outlined'
            ],
            color: [
                'primary',
                'light'
            ],
            shape: 'circle',
            disabled: false,
            loading: false,
            className: [
                'before:hover:scale-[1.035]'
            ]
        },
        {
            variant: [
                'filled',
                'outlined'
            ],
            color: [
                'primary',
                'light'
            ],
            disabled: false,
            loading: false,
            className: [
                'before:hover:drop-shadow',
                'before:ease-in-out'
            ]
        },
        {
            variant: 'outlined',
            color: 'transparent',
            disabled: false,
            loading: false,
            className: [
                'before:hover:drop-shadow',
                'before:ease-in-out'
            ]
        },
        {
            variant: 'filled',
            color: 'transparent',
            disabled: false,
            loading: false,
            className: [
                'before:ease-bounce-in',
                'before:scale-75',
                'before:hover:scale-100',
                'before:hover:bg-black',
                'before:hover:bg-opacity-[0.07]'
            ]
        },
        {
            variant: 'filled',
            color: 'primary',
            disabled: false,
            loading: false,
            className: [
                'before:hover:bg-primary-800'
            ]
        },
        {
            variant: 'filled',
            color: 'light',
            disabled: false,
            loading: false,
            className: [
                'before:hover:bg-gray-100'
            ]
        },
        {
            variant: 'outlined',
            color: 'primary',
            disabled: false,
            loading: false,
            className: [
                'before:hover:bg-primary-50'
            ]
        },
        {
            variant: 'outlined',
            color: 'light',
            disabled: false,
            loading: false,
            className: [
                'before:hover:bg-white/20'
            ]
        },
        {
            variant: 'outlined',
            color: 'transparent',
            disabled: false,
            loading: false,
            className: [
                'before:hover:bg-white/20'
            ]
        }
    ]
});
const configureDecorator = (decorator)=>/*#__PURE__*/ cloneElement(decorator, {
        color: 'white'
    });
/**
 * Button - [API](https://scl.sharp.com/?path=/docs/base-button) | [DSM](https://sharphealthcare.notion.site/Buttons-578bdc350e5e411fbd1d3c9b12597ac4)
 */ const Button = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { color = 'primary', variant = 'filled', size = 'md', shape = 'rounded', width = 'lg', loading = false, startDecorator, endDecorator, as, className, label, children } = _param, props = _object_without_properties(_param, [
        "color",
        "variant",
        "size",
        "shape",
        "width",
        "loading",
        "startDecorator",
        "endDecorator",
        "as",
        "className",
        "label",
        "children"
    ]);
    return /*#__PURE__*/ _jsx(ClassNameConfigurator, {
        disableDefaultClasses: true,
        children: /*#__PURE__*/ _jsxs(BaseButton, _object_spread_props(_object_spread({
            disabled: loading ? true : props.disabled,
            role: "button",
            ref: ref,
            slots: as && {
                root: prepareForSlot(as)
            },
            slotProps: {
                root: (buttonState)=>({
                        className: cn(classes({
                            variant,
                            color,
                            size,
                            width,
                            shape,
                            disabled: Boolean(buttonState.disabled),
                            loading,
                            className
                        }))
                    })
            }
        }, props), {
            children: [
                loading && /*#__PURE__*/ _jsx(Icon, {
                    icon: "spinner-third",
                    spin: true
                }),
                !(loading && shape === 'circle') && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        startDecorator && configureDecorator(startDecorator),
                        label !== null && label !== void 0 ? label : children,
                        endDecorator && configureDecorator(endDecorator)
                    ]
                })
            ]
        }))
    });
});
export { Button };
